import type { Brief, ItemRespaldo, Plano } from "../tipos";
import { beatsSonContiguos } from "../tipos";
import { formatNumero } from "./formatoNumero";

/**
 * Contenido interno de las seis etapas numeradas del brief — solo lo de
 * adentro de la caja; el borde, el número y el encabezado los pone
 * `EtapaBrief` desde `PiezaPage.tsx`.
 */

const claseChip = "rounded-full py-1 px-2.5 text-[11px] font-medium";

function formatItemRespaldo(item: ItemRespaldo): string {
  const valor = formatNumero(item.valor);
  if (item.comparado_con === undefined) return valor;
  const comparado = formatNumero(item.comparado_con);
  return item.contra_que ? `${valor} vs ${comparado} (${item.contra_que})` : `${valor} vs ${comparado}`;
}

// ---------- 1. Estrategia ----------

export const EstrategiaContenido = ({ estrategia }: { estrategia: Brief["estrategia"] }) => (
  <>
    <p className="text-[13px] leading-relaxed text-[rgb(var(--brand-black-rgb)_/_0.7)]">{estrategia.por_que}</p>

    {estrategia.respaldo.length > 0 && (
      <ul className="mt-4 flex flex-col gap-2">
        {estrategia.respaldo.map((item) => (
          <li
            key={item.fila_origen}
            className="rounded-xl bg-[rgb(var(--brand-primary-rgb)_/_0.05)] py-2.5 px-3.5 text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.75)]"
          >
            <span className="font-medium text-brand-deep">{item.metrica.replace(/_/g, " ")}</span>{" "}
            {formatItemRespaldo(item)}{" "}
            <span className="text-[rgb(var(--brand-black-rgb)_/_0.45)]">
              · {item.fuente.replace(/_/g, " ")} · {item.ventana.replace(/_/g, " ")}
            </span>
          </li>
        ))}
      </ul>
    )}

    {estrategia.duda && (
      <div className="mt-4 border-l-2 border-[rgb(var(--brand-black-rgb)_/_0.15)] pl-3 text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">
        {estrategia.duda}
      </div>
    )}
  </>
);

// ---------- 2. Dirección creativa ----------

export const DireccionCreativaContenido = ({
  direccion,
}: {
  direccion: Brief["direccion_creativa"];
}) => (
  <>
    <p className="text-[13px] leading-relaxed text-[rgb(var(--brand-black-rgb)_/_0.7)]">{direccion.direccion_visual}</p>
    {direccion.reglas_de_marca_aplicadas.length > 0 && (
      <div className="mt-4 flex flex-wrap gap-1.5">
        {direccion.reglas_de_marca_aplicadas.map((regla) => (
          <span key={regla} className={`${claseChip} bg-[rgb(var(--brand-black-rgb)_/_0.05)] text-[rgb(var(--brand-black-rgb)_/_0.65)]`}>
            {regla.replace(/_/g, " ")}
          </span>
        ))}
      </div>
    )}
  </>
);

// ---------- 3. Copy y guion ----------

const COLUMNAS_BEATS = ["#", "Segundos", "Texto en pantalla", "Qué se entiende", "Densidad subtítulo"];

export const GuionContenido = ({ guion }: { guion: Brief["guion"] }) => {
  const contiguos = beatsSonContiguos(guion.beats);

  return (
    <>
      <p className="rounded-xl bg-[rgb(var(--brand-black-rgb)_/_0.03)] py-3 px-4 text-[15px] font-semibold text-brand-black">
        “{guion.hook}”
      </p>

      {!contiguos && (
        <p className="mt-3 text-[12px] font-medium text-[#C33B36]">
          Los beats tienen huecos o se superponen en la línea de tiempo: revisar antes de producir.
        </p>
      )}

      {/* densidad_subtitulo es columna propia a propósito: vive por beat, no
          por pieza — el hook puede ir a dos palabras y la parte explicativa
          a cinco, en el mismo video. */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[560px] text-left text-[12px]">
          <thead>
            <tr className="border-b border-[#E2E4EA] text-[11px] uppercase tracking-wide text-[rgb(var(--brand-black-rgb)_/_0.45)]">
              {COLUMNAS_BEATS.map((columna) => (
                <th key={columna} className="py-2 pr-3 font-medium">
                  {columna}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {guion.beats.map((beat) => (
              <tr key={beat.n} className="border-b border-[#E2E4EA] last:border-0">
                <td className="py-2 pr-3 text-[rgb(var(--brand-black-rgb)_/_0.5)]">{beat.n}</td>
                <td className="py-2 pr-3 text-[rgb(var(--brand-black-rgb)_/_0.5)]">
                  {beat.desde_s}–{beat.hasta_s}s
                </td>
                <td className="py-2 pr-3 text-brand-black">{beat.texto_en_pantalla}</td>
                <td className="py-2 pr-3 text-[rgb(var(--brand-black-rgb)_/_0.65)]">{beat.que_se_entiende}</td>
                <td className="py-2 pr-3">
                  <span className={`${claseChip} bg-[rgb(var(--brand-primary-rgb)_/_0.08)] text-brand-deep`}>
                    {beat.densidad_subtitulo} palabras
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// ---------- 4. Plan de planos ----------

const ETIQUETAS_TIPO_PLANO: Record<Plano["tipo"], string> = {
  imagen_fija: "Imagen fija",
  clip: "Clip",
};

/**
 * `referencia_de` y `usa_referencia` son dos caras de la misma arista del
 * grafo de generación (una imagen se genera primero para mandarla como
 * referencia al modelo de video). Se combinan acá para no depender de que
 * los dos lados de una arista estén siempre completos en el dato.
 */
function construirMapaReferencias(planos: Plano[]): Map<string, string[]> {
  const mapa = new Map<string, Set<string>>();
  const agregarArista = (desde: string, hasta: string) => {
    if (!mapa.has(hasta)) mapa.set(hasta, new Set());
    mapa.get(hasta)!.add(desde);
  };
  for (const plano of planos) {
    if (plano.usa_referencia) agregarArista(plano.usa_referencia, plano.id);
    for (const hijo of plano.referencia_de ?? []) agregarArista(plano.id, hijo);
  }
  const resultado = new Map<string, string[]>();
  for (const [hasta, desdes] of mapa) resultado.set(hasta, [...desdes]);
  return resultado;
}

export const PlanDePlanosContenido = ({ planDePlanos }: { planDePlanos: Brief["plan_de_planos"] }) => {
  const referencias = construirMapaReferencias(planDePlanos.planos);

  return (
    <>
      {/* `total_planos` y `con_movimiento` son campos del dato, no se
          recalculan desde `planos`: si el backend algún día manda un total
          que no coincide con la lista, tiene que notarse acá, no taparse. */}
      <p className="mb-3 text-[12px] text-[rgb(var(--brand-black-rgb)_/_0.55)]">
        {planDePlanos.total_planos} {planDePlanos.total_planos === 1 ? "plano" : "planos"} ·{" "}
        {planDePlanos.con_movimiento} con movimiento
      </p>

      <ul className="flex flex-col gap-2">
        {planDePlanos.planos.map((plano) => {
          const desdes = referencias.get(plano.id) ?? [];
          return (
            <li key={plano.id} className="rounded-xl border border-[#E2E4EA] p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="rounded-md bg-[rgb(var(--brand-black-rgb)_/_0.06)] py-0.5 px-2 text-[11px] font-semibold text-[rgb(var(--brand-black-rgb)_/_0.7)]">
                    {plano.id}
                  </span>
                  <span className="text-[13px] text-brand-black">{plano.descripcion}</span>
                </div>
                <span
                  className={`${claseChip} ${
                    plano.tipo === "clip"
                      ? "bg-[rgb(var(--brand-accent-rgb)_/_0.25)] text-brand-deep"
                      : "bg-[rgb(var(--brand-black-rgb)_/_0.05)] text-[rgb(var(--brand-black-rgb)_/_0.6)]"
                  }`}
                >
                  {ETIQUETAS_TIPO_PLANO[plano.tipo]}
                </span>
              </div>
              {desdes.length > 0 && (
                <p className="mt-2 text-[11px] font-medium text-brand-deep">
                  {desdes.join(", ")} <span aria-hidden="true">→</span> {plano.id}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
};

// ---------- 5. Assets ----------

// Tipo literal derivado del contrato (no `string` suelto), igual que
// `ETIQUETAS_TIPO_PLANO` arriba y `TITULOS_ETAPA` en `PiezaPage.tsx`: si el
// contrato suma un cuarto estado, falta acá en compilación, no en runtime.
type EstadoAsset = Brief["assets"]["resumen"][number]["estado"];

const ETIQUETAS_ESTADO_ASSET: Record<EstadoAsset, { texto: string; clases: string }> = {
  aprobado: { texto: "Aprobado", clases: "bg-[#DDEFE7] text-[#1D7A57]" },
  rechazado: { texto: "Rechazado", clases: "bg-[#F8E3E2] text-[#C33B36]" },
  pendiente: { texto: "Pendiente", clases: "bg-[rgb(var(--brand-accent-rgb)_/_0.25)] text-brand-deep" },
};

export const AssetsContenido = ({ assets }: { assets: Brief["assets"] }) => (
  <ul className="flex flex-col gap-2">
    {assets.resumen.map((item) => (
      <li key={item.plano} className="rounded-xl border border-[#E2E4EA] p-3 text-[12px]">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="rounded-md bg-[rgb(var(--brand-black-rgb)_/_0.06)] py-0.5 px-2 text-[11px] font-semibold text-[rgb(var(--brand-black-rgb)_/_0.7)]">
              {item.plano}
            </span>
            <span className={`${claseChip} ${ETIQUETAS_ESTADO_ASSET[item.estado].clases}`}>
              {ETIQUETAS_ESTADO_ASSET[item.estado].texto}
            </span>
          </div>
          <span className="text-[rgb(var(--brand-black-rgb)_/_0.55)]">
            {item.intentos} {item.intentos === 1 ? "intento" : "intentos"} · {item.creditos} créditos
          </span>
        </div>
        {item.rechazo && <p className="mt-1.5 text-[#8A5A08]">{item.rechazo}</p>}
      </li>
    ))}
  </ul>
);

// ---------- 6. Creación de contenido ----------
// (la clave del brief y el componente siguen siendo `armado`: es contrato de
// datos, no el título visible)

export const ArmadoContenido = ({ armado }: { armado: Brief["armado"] }) => (
  <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
    {[
      { etiqueta: "Duración", valor: `${armado.duracion_s}s` },
      { etiqueta: "Relación", valor: armado.relacion },
      { etiqueta: "Subtítulos", valor: armado.subtitulos_quemados ? "Quemados" : "No quemados" },
      { etiqueta: "Música", valor: armado.musica ? armado.musica.replace(/_/g, " ") : "Sin música" },
    ].map((stat) => (
      <div key={stat.etiqueta} className="rounded-xl bg-[rgb(var(--brand-black-rgb)_/_0.03)] p-3">
        <dt className="text-[11px] text-[rgb(var(--brand-black-rgb)_/_0.5)]">{stat.etiqueta}</dt>
        <dd className="mt-0.5 text-[14px] font-semibold text-brand-black">{stat.valor}</dd>
      </div>
    ))}
  </dl>
);
